import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Text,
  Card,
  CardBody,
  Heading,
  Button,
  HStack,
  Spinner,
  Switch,
  Flex,
} from '@chakra-ui/react';

import { formatDateTime } from 'utils/date.util';

import { useColors } from './colors';
import {
  getStoreSyncConfigApi,
  updateStoreSyncConfigApi,
} from 'features/store-synchronization/store-synchronization.api';

const SynchronizationSchedule = () => {
  const dispatch = useDispatch();

  // Local state for form inputs
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState('AM');
  const [isEnabled, setIsEnabled] = useState(true);

  // Redux state
  const { syncConfig, getStoreSyncConfigStatus, updateStoreSyncConfigStatus } =
    useSelector((state) => state.storeSynchronization);

  const {
    cardBgColor,
    borderColor,
    inputColor,
    placeholderColor,
    textColor,
    inputBgColor,
    buttonPrimaryBg,
    buttonPrimaryHover,
    bgColor,
    hoverBg,
    previewBg: selectFocusBg,
  } = useColors();

  // Load configuration on component mount
  useEffect(() => {
    dispatch(getStoreSyncConfigApi());
  }, [dispatch]);

  // Update local state when config is loaded
  useEffect(() => {
    if (syncConfig?.dailyTimeSchedule) {
      setHour(syncConfig.dailyTimeSchedule.hour);
      setMinute(syncConfig.dailyTimeSchedule.minute);
      setPeriod(syncConfig.dailyTimeSchedule.period);
    }
    if (syncConfig?.isEnabled !== undefined) {
      setIsEnabled(syncConfig.isEnabled);
    }
  }, [syncConfig]);

  const handleSave = () => {
    const scheduleData = {
      dailyTimeSchedule: {
        hour: parseInt(hour),
        minute: parseInt(minute),
        period,
      },
      isEnabled,
    };
    dispatch(updateStoreSyncConfigApi(scheduleData));
  };

  const isLoading = getStoreSyncConfigStatus === 'pending';
  const isSaving = updateStoreSyncConfigStatus === 'pending';

  return (
    <Card
      bg={cardBgColor}
      borderRadius="xl"
      shadow="sm"
      border="1px"
      borderColor={borderColor}
      w="50%"
    >
      <CardBody p={6}>
        <VStack spacing={6} align="stretch">
          <Heading size="lg" color={textColor}>
            Synchronization Daily Schedule
          </Heading>

          <Text color={textColor} fontSize="md">
            Set the time for daily automatic synchronization of store data.
          </Text>

          {isLoading ? (
            <Box textAlign="center" py={4}>
              <Spinner color="blue.500" size="lg" />
              <Text mt={2} color={textColor}>
                Loading configuration...
              </Text>
            </Box>
          ) : (
            <>
              <FormControl>
                <Flex justify="space-between" align="center">
                  <FormLabel color={textColor} fontSize="md" fontWeight="medium" mb={0}>
                    Enable Daily Synchronization
                  </FormLabel>
                  <Switch
                    isChecked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                    size="lg"
                    colorScheme="blue"
                  />
                </Flex>
                <Text fontSize="sm" color={placeholderColor} mt={1}>
                  Turn on/off automatic daily synchronization
                </Text>
              </FormControl>

              <HStack spacing={4}>
                <FormControl flex="1">
                  <FormLabel color={textColor} fontSize="md" fontWeight="medium">
                    Hour
                  </FormLabel>
                  <Select
                    value={hour}
                    onChange={(e) => setHour(e.target.value)}
                    size="lg"
                    color={inputColor}
                    bg={inputBgColor}
                    borderColor={borderColor}
                    sx={{
                      option: {
                        bg: bgColor,
                        color: textColor,
                        _hover: {
                          bg: hoverBg + '!important',
                        },
                        _checked: {
                          bg: hoverBg + '!important',
                        },
                      },
                      _hover: {
                        bg: hoverBg,
                      },
                      _focus: {
                        bg: selectFocusBg,
                      },
                    }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl flex="1">
                  <FormLabel color={textColor} fontSize="md" fontWeight="medium">
                    Minute
                  </FormLabel>
                  <Select
                    value={minute}
                    onChange={(e) => setMinute(e.target.value)}
                    size="lg"
                    color={inputColor}
                    bg={inputBgColor}
                    borderColor={borderColor}
                    sx={{
                      option: {
                        bg: bgColor,
                        color: textColor,
                        _hover: {
                          bg: hoverBg + '!important',
                        },
                        _checked: {
                          bg: hoverBg + '!important',
                        },
                      },
                      _hover: {
                        bg: hoverBg,
                      },
                      _focus: {
                        bg: selectFocusBg,
                      },
                    }}
                  >
                    {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                      <option key={m} value={m}>
                        {m.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl flex="1">
                  <FormLabel color={textColor} fontSize="md" fontWeight="medium">
                    Period
                  </FormLabel>
                  <Select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    size="lg"
                    color={inputColor}
                    bg={inputBgColor}
                    borderColor={borderColor}
                    sx={{
                      option: {
                        bg: bgColor,
                        color: textColor,
                        _hover: {
                          bg: hoverBg + '!important',
                        },
                        _checked: {
                          bg: hoverBg + '!important',
                        },
                      },
                      _hover: {
                        bg: hoverBg,
                      },
                      _focus: {
                        bg: selectFocusBg,
                      },
                    }}
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </Select>
                </FormControl>
              </HStack>

              <HStack justify="flex-end" pt={2}>
                <Button
                  bg={buttonPrimaryBg}
                  color="white"
                  size="lg"
                  onClick={handleSave}
                  isLoading={isSaving}
                  loadingText="Saving..."
                  _hover={{
                    bg: buttonPrimaryHover,
                  }}
                  _active={{
                    bg: buttonPrimaryHover,
                  }}
                >
                  Save Schedule
                </Button>
              </HStack>
            </>
          )}

          {syncConfig?.updatedAt && (
            <Text fontSize="sm" color={placeholderColor}>
              Last updated: {formatDateTime(syncConfig.updatedAt)}
            </Text>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default SynchronizationSchedule;
